/**
 * Sistema de logging centralizado
 * Em produção, apenas erros críticos são logados
 * Em desenvolvimento/homolog, todos os logs são exibidos
 * 
 * Verifica tanto a variável de ambiente quanto a URL atual:
 * - Se for localhost ou contiver 'hml' na URL -> habilita logs
 * - Caso contrário, usa a configuração de ambiente
 */

import { isProduction } from '../config/environment';

type LogLevel = 'log' | 'warn' | 'error' | 'debug';

class Logger {
  /**
   * Verifica se está em ambiente de desenvolvimento/homolog baseado na URL
   */
  private isDevelopmentOrHomologUrl(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const hostname = window.location.hostname.toLowerCase();
    const href = window.location.href.toLowerCase();

    // Verificar se é localhost ou IP local
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.endsWith('.local')
    ) {
      return true;
    }

    // Verificar se contém 'hml' na URL (homolog)
    if (href.includes('hml') || hostname.includes('hml')) {
      return true;
    }

    return false;
  }

  private shouldLog(level: LogLevel): boolean {
    // Se for localhost ou homolog na URL, sempre logar
    if (this.isDevelopmentOrHomologUrl()) {
      return true;
    }

    // Em produção (baseado na variável de ambiente), apenas erros são logados
    if (isProduction) {
      return level === 'error';
    }

    // Em desenvolvimento/homolog (config), loga tudo
    return true;
  }

  /**
   * Log de informações gerais (desabilitado em produção)
   */
  log(...args: unknown[]): void {
    if (this.shouldLog('log')) {
      console.log(...args);
    }
  }

  /**
   * Log de avisos (desabilitado em produção)
   */
  warn(...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...args);
    }
  }

  /**
   * Log de erros (sempre logado, mesmo em produção)
   */
  error(...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(...args);
    }
  }

  /**
   * Log de debug (desabilitado em produção)
   */
  debug(...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log('[DEBUG]', ...args);
    }
  }

  /**
   * Log com prefixo de contexto (desabilitado em produção)
   */
  logWithContext(context: string, ...args: unknown[]): void {
    if (this.shouldLog('log')) {
      console.log(`[${context}]`, ...args);
    }
  }

  /**
   * Warn com prefixo de contexto (desabilitado em produção)
   */
  warnWithContext(context: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(`[${context}]`, ...args);
    }
  }
}

export const logger = new Logger();
